import React, { useEffect, useRef, useState } from 'react';

interface VideoFrameAtTimeProps {
  videoUrl: string;
  time: number;
  /** Shared by the clip strip: one sequential decoder and bounded frame cache. */
  extractFrame: (time: number) => Promise<string | null>;
  width?: number;
  height?: number;
  className?: string;
  style?: React.CSSProperties;
  rounded?: boolean;
  /** Skip new seeks while the editor preview is using the file. */
  suspend?: boolean;
}

/** Visible cards request a cached still during idle time. No per-card videos. */
const VideoFrameAtTime: React.FC<VideoFrameAtTimeProps> = ({
  videoUrl, time, extractFrame, width = 320, height = 180, className, style, rounded = true, suspend = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [result, setResult] = useState<{ url: string; time: number; frame: string | null } | null>(null);
  const current = result?.url === videoUrl && result.time === time ? result : null;

  useEffect(() => {
    if (!containerRef.current) return;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !videoUrl || suspend) return;
    let cancelled = false;
    const load = async () => {
      if (cancelled) return;
      const frame = await extractFrame(time).catch(() => null);
      if (!cancelled) setResult({ url: videoUrl, time, frame });
    };
    const idle = window.requestIdleCallback?.(() => { void load(); }, { timeout: 1500 });
    if (idle === undefined) void load();
    return () => {
      cancelled = true;
      if (idle !== undefined) window.cancelIdleCallback(idle);
    };
  }, [visible, videoUrl, time, extractFrame, suspend]);

  return (
    <div ref={containerRef} className={`relative overflow-hidden bg-muted ${rounded ? 'rounded' : ''} ${className ?? ''}`}
      style={{ aspectRatio: `${width} / ${height}`, ...style }}>
      {current?.frame && <img src={current.frame} alt="Video frame thumbnail" decoding="async" className="h-full w-full object-cover" />}
      {visible && !current && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      )}
    </div>
  );
};
export default VideoFrameAtTime;
